/* eslint-disable confluence-feature-gating/no-preconditioning */
import React, { Fragment, useEffect, useMemo } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import {
	QuickReloadSubscriber,
	ReloadType,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { ssrContentStateManager } from '@confluence/ssr-utilities/entry-points/SSRContentStateManager.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import { LazyEditorContentStateComponent } from '../lazyLoadables.ts';
import RendererContentStateQueryNode from '../RendererContentState/__generated__/RendererContentStateQuery.graphql.ts';
import type {
	RendererContentStateQuery$data,
	RendererContentStateQuery$variables,
} from '../RendererContentState/__generated__/RendererContentStateQuery.graphql.ts';

import { ContentStateStateContainer, defaultValues } from './ContentStateStateContainer.tsx';
import {
	type EditorContentStateQuery$data,
	type EditorContentStateQuery$variables,
} from './__generated__/EditorContentStateQuery.graphql.ts';
import type { ContentStateState, EditorContentStateProps } from './types.ts';

type ContentStateQueryData = NonNullable<
	EditorContentStateQuery$data | RendererContentStateQuery$data
>;

function computeContentStateValuesFromData(
	data: ContentStateQueryData,
	defaultStatesArray: ContentStateState['defaultStatesArray'],
	customStatesArray: ContentStateState['customStatesArray'],
	isDraft: boolean,
	contentId: string,
	isViewOnlyMode: boolean,
): ContentStateState {
	const newContentStateValues = Object.assign({}, defaultValues);
	/**  Computes content state values from query response data. Used for both SSR bootstrap (useEffect never runs on
   server, so we compute state from preloaded data for initial render) and client useEffect (setInitialValues).
   Setting shouldRunContentStateEffect = false prevents the effect from running again on parent re-renders
   (EditorContentState lives in breadcrumbs/title area), which would otherwise reset the values.
	*/
	newContentStateValues.shouldRunContentStateEffect = false;
	newContentStateValues.contentState = data?.singleContent?.contentState || null;
	newContentStateValues.contentStateLastUpdated =
		data?.singleContent?.contentStateLastUpdated?.value || null;
	newContentStateValues.defaultStatesArray = defaultStatesArray;
	newContentStateValues.customStatesArray = customStatesArray;
	// Only check availableContentStates when the user has edit permission.
	// In view-only mode, availableContentStates is either skipped via @skip directive
	// or absent entirely (renderer fallback query), so we must not treat its absence
	// as "content states disabled".
	if (!isViewOnlyMode) {
		const editorData = data as EditorContentStateQuery$data;
		const isSuggestedContentStatesDisabled =
			!editorData?.availableContentStates?.spaceContentStates;
		const isCustomContentStatesDisabled = !editorData?.availableContentStates?.customContentStates;
		const isContentStatesDisabled =
			isSuggestedContentStatesDisabled && isCustomContentStatesDisabled;
		if (isContentStatesDisabled) {
			newContentStateValues.isContentStateEnabled = false;
		} else if (isSuggestedContentStatesDisabled) {
			newContentStateValues.isSuggestedEnabled = false;
		} else if (isCustomContentStatesDisabled) {
			newContentStateValues.isCustomEnabled = false;
		}
	}
	newContentStateValues.lastFetchedIsDraft = isDraft;
	newContentStateValues.lastFetchedContentId = contentId;
	return newContentStateValues;
}

const EditorContentStateDataGetter = ({
	contentId,
	contentType,
	contentStateState,
	isPublishingContent,
	userOffline,
	onContentStateMenuToggle,
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState,
	isDetailsPanel,
	onClick,
	isViewMode,
	canEdit,
}: EditorContentStateProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCurrentPageLive = useIsCurrentPageLive();
	const isDraft = !isViewMode && !isCurrentPageLive;
	const { approvalRequest } = useApprovals();
	const hasApprovalStatus = !!approvalRequest?.application?.workflowStatusId;
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const isViewOnlyMode = !!(isViewMode && canEdit === false);

	const { data, error, refetch } = useQueryInterop<
		EditorContentStateQuery$data,
		EditorContentStateQuery$variables
	>(
		graphql`
			query EditorContentStateQuery(
				$contentId: ID!
				$isDraft: Boolean!
				$isApprovalsFgOn: Boolean!
				$format: GraphQLDateFormat!
				$shouldSkipContentStateLastUpdated: Boolean!
				$shouldSkipAvailableContentStates: Boolean = false
				$shouldSkipEditOnlyFields: Boolean = false
			) {
				singleContent(id: $contentId) {
					id
					contentState(isDraft: $isDraft) {
						id
						name
						color
						restrictionLevel
						isCallerPermitted @skip(if: $shouldSkipEditOnlyFields)
						lastModifierAaid @include(if: $isApprovalsFgOn)
					}
					# currently only view page and live pages require this field to be loaded for the content header
					# if the Approvals experiment is enabled, we include the content state last updated field
					contentStateLastUpdated(format: $format, isDraft: $isDraft)
						@skip(if: $shouldSkipContentStateLastUpdated) {
						value
					}
				}
				availableContentStates(contentId: $contentId) @skip(if: $shouldSkipAvailableContentStates) {
					customContentStates {
						name
						color
						id
					}
					spaceContentStates {
						name
						color
						id
						restrictionLevel
					}
				}
			}
		`,
		{
			fetchPolicy: window.__SSR_RENDERED__ ? 'cache-first' : 'network-only',
			variables: {
				contentId,
				isDraft,
				shouldSkipContentStateLastUpdated: expValEquals(
					'confluence_approvals_eap',
					'isEnabled',
					true,
				)
					? false
					: !isCurrentPageLive,
				isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
				format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
					? 'MILLIS'
					: 'USER_FRIENDLY',
				shouldSkipAvailableContentStates: isViewOnlyMode,
				shouldSkipEditOnlyFields: isViewOnlyMode,
			} as EditorContentStateQuery$variables,
			skip: expValEquals('confluence_approvals_eap', 'isEnabled', true)
				? false
				: !contentStateState.state.shouldRunContentStateEffect,
		},
	);

	// Fallback query for view-only users: reuse the RendererContentStateQuery which doesn't
	// require edit permission. This query is already preloaded in preloadContentHeader,
	// so cache-first will read from the existing cache entry without making a new network request.
	const { data: rendererData } = useQueryInterop<
		RendererContentStateQuery$data,
		RendererContentStateQuery$variables
	>(RendererContentStateQueryNode, {
		fetchPolicy: 'cache-first',
		variables: {
			contentId,
			isDraft,
			// Use 'USER_FRIENDLY' to match preloadRendererContentState variables,
			// ensuring cache-first hits the preloaded cache entry.
			format: 'USER_FRIENDLY',
		} as RendererContentStateQuery$variables,
		skip: !isViewOnlyMode,
	});

	// Use renderer fallback data when editor query has no data (permission error for view-only users).
	// During client hydration, the renderer query hook may not return data synchronously even with
	// cache-first (Relay interop is async). Fall back to window.__SSR_CONTENT_STATE__ which was
	// serialized by the server and is available synchronously, ensuring hydration output matches SSR.
	const ssrContentStateData =
		renderServerPlaceholder && isViewOnlyMode && !data && !rendererData
			? (window as any).__SSR_CONTENT_STATE__
			: null;
	const effectiveData = data ?? (isViewOnlyMode ? (rendererData ?? ssrContentStateData) : null);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && effectiveData) {
		ssrContentStateManager.setContentStateData(effectiveData);
	}

	// Memoize filtered arrays to avoid expensive operations during render
	// Use `data` (editor query) directly — availableContentStates only exists on EditorContentStateQuery$data,
	// not on the RendererContentState fallback (which has no editable states for view-only users).
	const defaultStatesArray = useMemo(
		() =>
			data?.availableContentStates?.spaceContentStates?.filter(
				(i): i is NonNullable<typeof i> => !!i,
			) || [],
		[data?.availableContentStates?.spaceContentStates],
	);
	const customStatesArray = useMemo(
		() =>
			data?.availableContentStates?.customContentStates?.filter(
				(i): i is NonNullable<typeof i> => !!i,
			) || [],
		[data?.availableContentStates?.customContentStates],
	);

	/**  During SSR, useEffect never runs. When we have preloaded data, compute bootstrap state
	so shouldRenderUI and ContentStateButton receive correct contentState. */
	let ssrContentState: ContentStateState | null = null;
	// Run SSR bootstrap for all users on the server AND during client hydration.
	// For view-only users, use renderServerPlaceholder (true on both server and hydration)
	// to ensure both renders produce the same output, preventing hydration mismatches.
	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		(process.env.REACT_SSR || (renderServerPlaceholder && isViewOnlyMode)) &&
		effectiveData &&
		effectiveData.singleContent?.id === contentId &&
		contentStateState.state.lastFetchedIsDraft === null &&
		(expValEquals('confluence_approvals_eap', 'isEnabled', true) ||
			contentStateState.state.shouldRunContentStateEffect)
	) {
		ssrContentState = computeContentStateValuesFromData(
			effectiveData,
			defaultStatesArray,
			customStatesArray,
			isDraft,
			contentId,
			isViewOnlyMode,
		);
	}

	// SSR fallback: when the preloaded query fails (e.g. PermissionException for
	// view-only users), Apollo doesn't cache the error so the hook returns no data
	// and no error. Settle the container so shouldRenderUI can pass and the
	// (separately-fetched) approval indicator can render.
	// Use renderServerPlaceholder (true on server AND during client hydration) so
	// both renders produce the same output, avoiding a hydration mismatch.
	if (
		renderServerPlaceholder &&
		!ssrContentState &&
		!effectiveData &&
		(hasApprovalStatus || isViewOnlyMode) &&
		contentStateState.state.lastFetchedIsDraft === null &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true)
	) {
		ssrContentState = {
			...defaultValues,
			isContentStateEnabled: false,
			shouldRunContentStateEffect: false,
			lastFetchedIsDraft: isDraft,
			lastFetchedContentId: contentId,
		};
	}

	const effectiveState = ssrContentState ?? contentStateState.state;

	const isTransitioning =
		// Content State has been fetched before
		contentStateState.state.lastFetchedIsDraft !== null &&
		// for a mode that was different from the current mode
		contentStateState.state.lastFetchedIsDraft !== isDraft &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);

	useEffect(() => {
		if (isTransitioning) {
			void contentStateState.resetToDefaults();
		}
	}, [isTransitioning, contentStateState]);

	// Settle the container when the query errors before any data arrives, so
	// `shouldRenderUI` can release and the (separately-fetched) approval indicator can render.
	useEffect(() => {
		if (!isTransitioning && error) {
			if (isViewOnlyMode && effectiveData) {
				// The editor query failed with a PermissionException but the renderer fallback
				// has data — mark the error as handled to suppress monitoring noise.
				markErrorAsHandled(error);
			} else if (
				!effectiveData &&
				contentStateState.state.shouldRunContentStateEffect &&
				contentStateState.state.lastFetchedIsDraft === null
			) {
				markErrorAsHandled(error);
				void contentStateState.setInitialValues({
					...defaultValues,
					isContentStateEnabled: false,
					shouldRunContentStateEffect: false,
					lastFetchedIsDraft: isDraft,
					lastFetchedContentId: contentId,
				});
			}
		}
	}, [
		error,
		effectiveData,
		contentStateState,
		isDraft,
		contentId,
		isTransitioning,
		isViewOnlyMode,
	]);

	useEffect(() => {
		// When experiment is off, this is always false
		if (isTransitioning) {
			return;
		}
		// When Approvals experiment is on, we only render EditorContentState in the Byline (Breadcrumbs), so
		// we do not get same-page remounts. This means we can safely assign new values any time new data is available.
		// Also verify the data belongs to the current contentId — after a navigation, the query may
		// briefly return cached/stale data from the previous contentId before the new network response arrives.
		if (
			effectiveData &&
			effectiveData.singleContent?.id === contentId &&
			(expValEquals('confluence_approvals_eap', 'isEnabled', true) ||
				contentStateState.state.shouldRunContentStateEffect)
		) {
			// When a mutation is in flight (isUpdating=true), skip this setInitialValues call
			// to prevent stale refetch responses from clobbering the optimistic content state.
			// Mutations set isUpdating=true before firing, and onCompleted no longer resets it.
			// Instead, we reset isUpdating here so the next data change (the correct refetch response)
			// will apply normally via setInitialValues.
			if (contentStateState.state.isUpdating) {
				void contentStateState.setIsUpdating(false);
				return;
			}
			const computed = computeContentStateValuesFromData(
				effectiveData,
				defaultStatesArray,
				customStatesArray,
				isDraft,
				contentId,
				isViewOnlyMode,
			);
			void contentStateState.setInitialValues(computed);
		}
	}, [
		effectiveData,
		contentStateState,
		defaultStatesArray,
		customStatesArray,
		isDraft,
		isTransitioning,
		contentId,
		isViewOnlyMode,
	]);

	const shouldRenderUI =
		!isTransitioning &&
		// Don't render stale content state from a different page during cross-page navigation.
		!(
			effectiveState.lastFetchedContentId !== null &&
			effectiveState.lastFetchedContentId !== contentId &&
			fg('confluence_frontend_transition_preloading_system')
		) &&
		// Don't render the content state UI until data has been fetched for the current context.
		// Use effectiveState so SSR bootstrap (preloaded data) works before setState flushes.
		!(effectiveState.shouldRunContentStateEffect && effectiveState.lastFetchedIsDraft === null) &&
		// When the space setting contentStatesAllowed is false (isContentStateEnabled = false),
		// hide the status button entirely unless the page already has a content state or approval status.
		// Pages with an existing status still show the button so users can see/manage the current state.
		!(
			!effectiveState.isContentStateEnabled &&
			!effectiveState.contentState &&
			!hasApprovalStatus &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		) &&
		// In view mode, don't render the empty Status button for users without edit permission.
		!(
			isViewMode &&
			!canEdit &&
			!(effectiveState.contentState || hasApprovalStatus) &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		) &&
		// In view mode, don't render the empty Status button for blog posts without a content state.
		!(
			isViewMode &&
			contentType === 'blogpost' &&
			!(effectiveState.contentState || hasApprovalStatus) &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		) &&
		// In both view and edit mode, don't render the empty Status button
		// when no content state or approval status exists yet. First-time status selection is handled
		// by ContentStateHoverButton in the floating toolbar (edit mode only).
		// Don't render the empty Status button when no content state or approval status exists.
		// This also prevents a hydration mismatch: the server renders no content for pages without
		// a status, and the client must match during hydration.
		// First-time status selection is handled by ContentStateHoverButton in the floating toolbar
		// (edit mode only).
		// Exception: the details panel renders its own "+" empty-state button via
		// `renderPlusButtonEmptyState`, so we must still render the UI in that case to allow
		// users to add a status from the details panel (PGXT-10232).
		!(
			!(effectiveState.contentState || hasApprovalStatus) &&
			!floatingToolbarButtonStyle &&
			!renderPlusButtonEmptyState &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true)
		);

	if (shouldRenderUI && isPublishingContent) {
		// isPublishingContent is an optional prop that we only pass to this component if a page has a content state.
		// Therefore if it's true, means we're publishing a page with a content state so fire the analytic event
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'publishedWithContentState',
				actionSubject: 'page',
				objectId: contentId,
				source: 'editorContentState',
				attributes: {
					contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
				},
			},
		}).fire();
	}

	return (
		<Fragment>
			{isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
				<QuickReloadSubscriber name="content-state" types={[ReloadType.content]} reload={refetch} />
			)}
			{shouldRenderUI && (
				<LazyEditorContentStateComponent
					contentId={contentId}
					contentType={contentType}
					contentStateState={
						{ ...contentStateState, state: effectiveState } as ContentStateStateContainer
					}
					userOffline={userOffline}
					onContentStateMenuToggle={onContentStateMenuToggle}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					renderPlusButtonEmptyState={renderPlusButtonEmptyState}
					isDetailsPanel={isDetailsPanel}
					onClick={onClick}
					isViewMode={isViewMode}
					canEdit={canEdit}
				/>
			)}
		</Fragment>
	);
};

const EditorContentStateWithErrorBoundary = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(EditorContentStateDataGetter);

const contentStateContainers = [ContentStateStateContainer];

export const EditorContentState = (props: any): React.JSX.Element => (
	<Subscribe to={contentStateContainers}>
		{(contentStateState: ContentStateStateContainer) => (
			<EditorContentStateWithErrorBoundary {...props} contentStateState={contentStateState} />
		)}
	</Subscribe>
);
